import { useEffect, useRef, useState } from "react";
import { Waves, Volume2, VolumeX, Vibrate } from "lucide-react";

/**
 * Surge Feedback — optional subtle haptic + spatial click tone during the
 * CTA surge. Reads/writes user prefs in localStorage; listens for the
 * `kaf:surge` window event (dispatched by primary CTAs) and, when enabled,
 * fires a soft WebAudio bloom panned to the click position plus a light
 * navigator.vibrate pattern. Both are opt-in and honor reduced-motion.
 *
 * Prefs:
 *   kaf:surge-sound   -> "1" | "0" (default "0")
 *   kaf:surge-haptics -> "1" | "0" (default "0")
 */

const KEY_SOUND = "kaf:surge-sound";
const KEY_HAPTIC = "kaf:surge-haptics";

function readPref(k: string): boolean {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(k) === "1"; } catch { return false; }
}
function writePref(k: string, v: boolean) {
  try { window.localStorage.setItem(k, v ? "1" : "0"); } catch { /* ignore */ }
}

export function SurgeFeedback() {
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useState(false);
  const [haptic, setHaptic] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Hydrate prefs after mount to avoid SSR mismatch.
  useEffect(() => {
    setSound(readPref(KEY_SOUND));
    setHaptic(readPref(KEY_HAPTIC));
  }, []);

  useEffect(() => { writePref(KEY_SOUND, sound); }, [sound]);
  useEffect(() => { writePref(KEY_HAPTIC, haptic); }, [haptic]);

  useEffect(() => {
    const supportsVibrate = typeof navigator !== "undefined" && "vibrate" in navigator;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const getCtx = (): AudioContext | null => {
      if (audioCtxRef.current) return audioCtxRef.current;
      const AC =
        (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
          .AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      audioCtxRef.current = new AC();
      return audioCtxRef.current;
    };

    const playTone = (x: number) => {
      const ctx = getCtx();
      if (!ctx) return;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const now = ctx.currentTime;

      // Stereo pan from click position (-1 left … +1 right).
      const w = window.innerWidth || 1;
      const pan = Math.max(-1, Math.min(1, (x / w) * 2 - 1));

      const master = ctx.createGain();
      master.gain.setValueAtTime(0.0001, now);
      master.gain.exponentialRampToValueAtTime(0.14, now + 0.06);
      master.gain.exponentialRampToValueAtTime(0.0001, now + 2.2);

      const panner = ctx.createStereoPanner
        ? ctx.createStereoPanner()
        : null;
      if (panner) panner.pan.value = pan * 0.6;

      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.setValueAtTime(1800, now);
      lp.frequency.exponentialRampToValueAtTime(600, now + 2.0);
      lp.Q.value = 0.7;

      // Two soft partials — a warm, bell-like bloom.
      const partials: [number, number][] = [
        [220, 0.8],
        [330, 0.5],
        [523.25, 0.25],
      ];
      const stops: OscillatorNode[] = [];
      for (const [freq, amp] of partials) {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, now);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.985, now + 1.6);
        const g = ctx.createGain();
        g.gain.setValueAtTime(amp, now);
        osc.connect(g).connect(lp);
        osc.start(now);
        osc.stop(now + 2.3);
        stops.push(osc);
      }

      if (panner) {
        lp.connect(panner).connect(master).connect(ctx.destination);
      } else {
        lp.connect(master).connect(ctx.destination);
      }
    };

    const onSurge = (e: Event) => {
      const ce = e as CustomEvent<{ x?: number; y?: number }>;
      const x = ce.detail?.x ?? window.innerWidth / 2;

      // Read fresh prefs each fire — user may have toggled between clicks.
      const wantSound = readPref(KEY_SOUND);
      const wantHaptic = readPref(KEY_HAPTIC);

      if (wantHaptic && supportsVibrate && !reduce) {
        try { navigator.vibrate([8, 40, 14, 60, 6]); } catch { /* ignore */ }
      }
      if (wantSound) playTone(x);
    };

    window.addEventListener("kaf:surge", onSurge);
    return () => window.removeEventListener("kaf:surge", onSurge);
  }, []);

  return (
    <div className="fixed bottom-24 right-4 z-40 sm:bottom-6 sm:right-24">
      {open && (
        <div
          role="dialog"
          aria-label="Surge feedback preferences"
          className="mb-2 w-64 rounded-2xl border hairline bg-charcoal/95 p-3 text-xs text-ivory shadow-halo backdrop-blur-md animate-rise"
        >
          <p className="mb-2 px-1 text-[10px] uppercase tracking-[0.3em] text-gold">
            Surge feedback
          </p>
          <PrefRow
            icon={sound ? <Volume2 size={13} /> : <VolumeX size={13} />}
            label="Spatial tone"
            hint="Soft bell bloom on primary actions"
            checked={sound}
            onChange={setSound}
          />
          <PrefRow
            icon={<Vibrate size={13} />}
            label="Haptic pulse"
            hint="Subtle vibration on supporting devices"
            checked={haptic}
            onChange={setHaptic}
          />
          <p className="mt-2 px-1 text-[10px] leading-relaxed text-muted-foreground">
            Both are muted by default. Reduced-motion mutes haptics automatically.
          </p>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Surge feedback preferences"
        className="inline-flex items-center gap-2 rounded-full border hairline bg-charcoal/80 px-3 py-2 text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-md transition-colors hover:border-gold hover:text-gold-luminous focus:outline-none focus:ring-2 focus:ring-gold"
      >
        <Waves size={12} aria-hidden />
        Feedback
      </button>
    </div>
  );
}

function PrefRow({
  icon, label, hint, checked, onChange,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg px-1 py-2 hover:bg-obsidian/40">
      <span className="mt-0.5 text-gold-luminous">{icon}</span>
      <span className="flex-1">
        <span className="block text-[12px] text-ivory">{label}</span>
        <span className="block text-[10px] text-muted-foreground">{hint}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`mt-1 inline-flex h-4 w-7 shrink-0 items-center rounded-full border hairline transition-colors ${
          checked ? "bg-gold-gradient" : "bg-obsidian"
        }`}
      >
        <span
          className={`inline-block h-3 w-3 transform rounded-full bg-ivory shadow transition-transform ${
            checked ? "translate-x-3.5" : "translate-x-0.5"
          }`}
        />
      </button>
    </label>
  );
}
